import { Injectable, OnDestroy, LOCALE_ID, Inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { FormBuilder, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';

import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { State } from '../../../../redux/reducers/invoices/reducers-segments-options';
import { Change, Apply, Reset } from '../../../../redux/actions/invoices/actions-segments-options';
import { Options } from '../../../../redux/models/invoices/segments.options.model';
import { getSegmentsOptions } from '../../../../redux/reducers';

@Injectable()
export class SegmentsFiltersService implements OnDestroy {
  private readonly defaultDateFrom: IDate;
  private readonly defaultDateTo: IDate;
  private stateSubscription: Subscription;
  /* eslint-disable */
  private stateValueHandler: Function;
  private previousSegment: string;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly store: Store<State>,
    @Inject(LOCALE_ID) private readonly locale: string
  ) {
    const today = new Date();
    this.defaultDateFrom = {
      year: today.getFullYear(),
      month: today.getMonth()
    };
    this.defaultDateTo = {
      year: today.getFullYear(),
      month: today.getMonth() + 1
    };
    this.stateValueHandler = () => {};
  }

  private static fromAllValue(value: any) {
    if ('All' === value) {
      return null;
    }
    return value;
  }
  private static toAllValue(value: any) {
    if (!value) {
      return 'All';
    }
    return value;
  }

  private static stringDateToMonthValue(dateString: string): IDate {
    if (!dateString) {
      return null;
    }
    const isoDataParts = dateString.split('-').map(Number);
    if (isoDataParts.length < 2) {
      return null;
    }
    return { year: isoDataParts[0], month: isoDataParts[1] };
  }
  private static monthValueToDateString(value: IDate, lastDay = false): string {
    if (!value.year || !value.month) {
      return '';
    }
    const date = new Date();
    // zero day means last day of previous month. month is zero-based in Date object
    const day = lastDay ? 0 : 1;
    const month = lastDay ? value.month : value.month - 1;
    const twoDigits = v => v.toString().padStart(2, '0');
    date.setFullYear(value.year, month, day);
    return `${value.year}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;
  }

  /* eslint-disable */
  registerStateHandler(f: Function) {
    this.initStateSubscription();
    this.stateValueHandler = f;
  }

  createFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      dateFrom: [this.defaultDateFrom],
      dateTo: [this.defaultDateTo],
      offices: [null, Validators.required],
      segment: ['All'],
      provider: [{ value: 'All', disabled: true }]
    });
  }

  convertStateToFormValue(optionsState: Options) {
    return {
      ...optionsState,
      dateFrom: SegmentsFiltersService.stringDateToMonthValue(optionsState.dateFrom),
      dateTo: SegmentsFiltersService.stringDateToMonthValue(optionsState.dateTo),
      segment: SegmentsFiltersService.toAllValue(optionsState.segment),
      provider: SegmentsFiltersService.toAllValue(optionsState.provider)
    };
  }

  formValueChangeHandler(formValue: any) {
    const dateFrom = formValue.dateFrom
      ? SegmentsFiltersService.monthValueToDateString(formValue.dateFrom)
      : formValue.dateFrom;
    const dateTo = formValue.dateTo
      ? SegmentsFiltersService.monthValueToDateString(formValue.dateTo, true)
      : formValue.dateTo;
    const provider = formValue.segment === this.previousSegment ? formValue.provider : 'All';
    this.previousSegment = formValue.segment;
    return {
      ...formValue,
      dateFrom,
      dateTo,
      segment: SegmentsFiltersService.fromAllValue(formValue.segment),
      provider: SegmentsFiltersService.fromAllValue(provider)
    };
  }

  update(options: Options, apply: boolean) {
    this.store.dispatch(new Change({ options, apply }));
  }

  apply() {
    this.store.dispatch(new Apply());
  }

  reset(options: Options) {
    this.store.dispatch(new Reset({ options: { ...options } }));
  }

  formValueToBadgeLabels(formValue: any, officesLength: number): string[] {
    const labels = [];
    if (formValue.dateFrom && formValue.dateTo) {
      const dateFrom = new Date(formValue.dateFrom.year, formValue.dateFrom.month - 1);
      const dateTo = new Date(formValue.dateTo.year, formValue.dateTo.month - 1);
      const accountInvoicesFilterValueDates = $localize`:@@segments-filters.accountInvoicesFilterValueDates:from {0} to {1}`;
      labels.push(
        accountInvoicesFilterValueDates,
        formatDate(dateFrom, 'MMM y', this.locale),
        formatDate(dateTo, 'MMM y', this.locale)
      );
    }
    const selectedOfficesCount = formValue.offices.length;
    if (selectedOfficesCount > 0 && selectedOfficesCount < officesLength) {
      const accountInvoicesFilterValueOffices = $localize`:@@segments-filters.accountInvoicesFilterValueOffices:{0} offices`;
      labels.push(accountInvoicesFilterValueOffices);
    }
    if (formValue.segment !== 'All') {
      const accountInvoicesFilterValueSegments = $localize`:@@segments-filters.accountInvoicesFilterValueSegments:{0} segments`;
      labels.push(accountInvoicesFilterValueSegments, formValue.segment);
    }
    if (formValue.provider !== 'All') {
      labels.push(formValue.provider);
    }
    return labels;
  }

  ngOnDestroy() {
    this.stateSubscription.unsubscribe();
  }

  validateDateLength(control: AbstractControl) {
    const accountInvoicesValidationFromDate = $localize`:@@segments-filters.accountInvoicesValidationFromDate:No data available for the selected month yet`;
    if (!control.value || !control.value.month || control.value.year > 999) {
      return null;
    }
    return {
      validateDateLength: accountInvoicesValidationFromDate
    };
  }

  createDateFromValidator(maxDate: IDate) {
    const accountInvoicesValidationFromDate = $localize`:@@segments-filters.accountInvoicesValidationFromDate:No data available for the selected month yet`;
    return (control: AbstractControl) => {
      if (
        !control.value ||
        !control.value.month ||
        control.value.year < maxDate.year ||
        (control.value.year === maxDate.year && control.value.month <= maxDate.month)
      ) {
        return null;
      }
      return {
        validateFromDate: accountInvoicesValidationFromDate
      };
    };
  }

  getDateErrorMessage(control: AbstractControl): string {
    if (!control.touched || !control.errors) {
      return '';
    }
    if (control.errors.inputError !== undefined) {
      const validateDateFormat = $localize`:@@segments-filters.validateDateFormat:Please enter a valid date format`;
      // default monthpicker error
      return validateDateFormat;
    }
    // custom errors
    const error = control.errors[Object.keys(control.errors)[0]];
    return typeof error === 'string' ? error : '';
  }

  private initStateSubscription() {
    if (!this.stateSubscription) {
      this.stateSubscription = this.store
        .pipe(
          select(getSegmentsOptions),
          map(optionsState => [this.convertStateToFormValue(optionsState.data), optionsState.apply])
        )
        .subscribe(([formValue, apply]) => this.stateValueHandler(formValue, apply));
    }
  }
}

export interface IOption {
  value: string;
  label: string;
}
export interface IDate {
  year: number;
  month: number;
}
